// useReportStatus.ts - Updated version
import { useCallback, useState } from "react";
import { ReportStatusType } from "../../types";
import { useToast } from "../../context/ToastContext";
import useFetchWithAuth from "./useFetchWithAuth";
import { getApiUrl } from "../../utils/api";

export const useReportStatus = (
  reportId: string | undefined,
  initialStatus: ReportStatusType | undefined,
  onStatusUpdate?: (reportId: string, newStatus: ReportStatusType) => void
) => {
  const { toast } = useToast();
  const { fetchWithAuth } = useFetchWithAuth();
  const [status, setStatus] = useState<ReportStatusType | undefined>(
    initialStatus
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetStatus = useCallback(
    async (newStatus: ReportStatusType) => {
      const toastSuccess = () => {
        toast({
          type: "success",
          title: "Success",
          subtitle: "Report status changed successfully!",
        });
      };

      if (!reportId || !newStatus || !status) return;

      try {
        setLoading(true);
        await fetchWithAuth(getApiUrl(`/admin/reports/${reportId}/status`), {
          method: "PATCH",
          body: JSON.stringify({ status: newStatus }),
        });

        setStatus(newStatus);

        // Call the callback to update the parent component's state
        if (onStatusUpdate) {
          onStatusUpdate(reportId, newStatus);
        }

        toastSuccess();
      } catch (error) {
        setError("Failed to update the status. Please try again.");
        setStatus(status); // Revert to previous status if the update fails
      } finally {
        setLoading(false);
      }
    },
    [reportId, status, toast, onStatusUpdate, fetchWithAuth]
  );

  const clearError = () => setError(null);

  return { status, setStatus, loading, error, handleSetStatus, clearError };
};
